import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Skeleton } from '../../components/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfluencerAuth } from '../../contexts/InfluencerAuthContext';
import { CheckCircle, Error as ErrorIcon } from '../../mui-icons';
import './InfluencerVerifyPage.css';
import logoIcon from '../../assets/icon-no-name.webp';

const InfluencerVerifyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyToken, isAuthenticated } = useInfluencerAuth();

  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const [hasMounted, setHasMounted] = useState(false);
  const hasVerified = useRef(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (!hasMounted) return;

    const token = searchParams.get('token');
    if (hasVerified.current) return;

    const existingToken = localStorage.getItem('influencer_token');
    if (existingToken || isAuthenticated) {
      navigate('/influencer', { replace: true });
      return;
    }

    if (!token) {
      setStatus('error');
      setError('Link inválido. Nenhum token encontrado.');
      return;
    }

    if (token.length < 32 || token.length > 100) {
      setStatus('error');
      setError('Token com formato inválido. Solicite um novo link.');
      return;
    }

    hasVerified.current = true;

    const verify = async () => {
      try {
        await verifyToken(token);
        setStatus('success');
        window.history.replaceState({}, '', '/influencer/verify');
        setTimeout(() => {
          navigate('/influencer', { replace: true });
        }, 2000);
      } catch (err) {
        console.error('Erro na verificação:', err);
        handleError(err.message);
      }
    };

    verify();
  }, [hasMounted, searchParams, navigate, isAuthenticated, verifyToken]);

  const handleError = (message) => {
    setStatus('error');

    if (message === 'Token já utilizado' || message?.includes('já utilizado')) {
      const jwt = localStorage.getItem('influencer_token');
      if (jwt) {
        navigate('/influencer', { replace: true });
        return;
      }
      setError('Este link já foi utilizado. Solicite um novo link de acesso.');
    } else if (message === 'Token expirado' || message?.includes('expirado')) {
      setError('Este link expirou (válido por 15 minutos). Solicite um novo link de acesso.');
    } else if (message === 'Token inválido ou expirado' || message?.includes('inválido')) {
      setError('Link inválido ou expirado. Solicite um novo link de acesso.');
    } else if (message === 'Conta desativada' || message?.includes('desativada')) {
      setError('Sua conta está desativada. Entre em contato com o suporte.');
    } else if (message?.includes('conexão') || message?.includes('rede')) {
      setError('Erro de conexão. Verifique sua internet e tente novamente.');
    } else if (
      message?.includes('Erro interno') ||
      message?.includes('Problema temporário no servidor') ||
      message?.includes('Resposta inválida do servidor')
    ) {
      setError('Problema temporário no servidor. Tente novamente ou solicite um novo link.');
    } else {
      setError(message || 'Erro ao verificar acesso. Tente novamente.');
    }
  };

  return (
    <div className="influencer-verify-page surface-app">
      <div className="influencer-verify-container">
        <Card variant="auth" className="influencer-verify-card">
          <div className="verify-logo">
            <img src={logoIcon} alt="PersonalNutri" className="verify-logo-img" />
          </div>

          {(!hasMounted || status === 'verifying') && (
            <div className="verify-status verifying">
              <Skeleton width={64} height={64} borderRadius={32} style={{ margin: '0 auto 16px' }} />
              <Skeleton width="60%" height={24} borderRadius={4} style={{ margin: '0 auto 12px' }} />
              <Skeleton width="80%" height={16} borderRadius={4} style={{ margin: '0 auto' }} />
            </div>
          )}

          {hasMounted && status === 'success' && (
            <div className="verify-status success">
              <div className="status-icon">
                <CheckCircle sx={{ fontSize: 64, color: 'var(--color-primary)' }} />
              </div>
              <h2>Acesso verificado!</h2>
              <p>Você será redirecionado em instantes...</p>
            </div>
          )}

          {hasMounted && status === 'error' && (
            <div className="verify-status error">
              <div className="status-icon">
                <ErrorIcon sx={{ fontSize: 64, color: 'var(--color-error)' }} />
              </div>
              <h2>Erro na verificação</h2>
              <p>{error}</p>
              <Button variant="primary" onClick={() => navigate('/influencer/login')}>
                Solicitar novo link
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default InfluencerVerifyPage;
